/**
 * The real OnlineClient (ARCHITECTURE §6–7): lobby (queue / rooms / ready / ratings / leaderboard), WebRTC
 * signaling with perfect negotiation, automatic relay fallback after P2P_CONNECT_TIMEOUT_MS (or as soon as ICE
 * fails), the relay kept on standby under a direct link (so a silent DataChannel fails over in ~1.5 s), match handles (ready → match_start, result report, rematch on the same transport), and `ui.net`
 * mirroring.
 *
 * One `MatchConnection` lives per matchId and survives rematches (same id, same open transport); each game gets
 * a fresh `OnlineMatchHandle`.
 */
import {
  P2P_CONNECT_TIMEOUT_MS,
  PROTOCOL_VERSION,
  PlayerNameSchema,
  SIM_CONTENT_HASH,
  type BinaryChannel,
  type CharacterId,
  type MatchResult,
  type MessageOf,
  type QueueMode,
  type RatingInfo,
  type RoomSettings,
  type ServerMessage,
  type ServerMessageType,
  type SignalData,
  type Transport,
  type TransportKind,
} from '@hf/shared';
import { createRelayTransport, createWebRtcTransport } from '@hf/netcode';
import type { UiStore } from '../ui-model/store';
import type { NetUiState } from '../ui-model/types';
import { getPlatform } from '../platform';
import { clientVersionFor, type NetEnv, type TimerId } from './env';
import { HybridTransport } from './hybrid-transport';
import { PeerLink } from './peer';
import { LobbySocket, type HelloMessage, type SocketState } from './socket';
import type {
  LeaderboardEntry,
  PlayerActionResult,
  PlayerReportReason,
  MatchFound,
  MatchResultMessage,
  OnlineClient,
  OnlineMatchHandle,
  OpponentLeftMessage,
  RematchStateMessage,
} from './types';

/** Request/response timeouts for leaderboard / rating queries. */
export const REQUEST_TIMEOUT_MS = 8_000;
/**
 * A started game whose connection closed before its verdict (the opponent left → `opponent_left` precedes the
 * forfeit `match_result`; we quit → the server still sends ours) keeps listening for its `match_result` this long.
 */
export const VERDICT_WAIT_MS = 30_000;

type Listener<T> = (v: T) => void;

class Emitter<T> {
  private readonly set = new Set<Listener<T>>();
  on(h: Listener<T>): () => void {
    this.set.add(h);
    return () => void this.set.delete(h);
  }
  emit(v: T): void {
    for (const h of [...this.set]) {
      try {
        h(v);
      } catch (e) {
        console.warn('[net] listener threw', e);
      }
    }
  }
  clear(): void {
    this.set.clear();
  }
}

// ── Match handle (one per game) ─────────────────────────────────────────────────────────────────────────

class MatchHandle implements OnlineMatchHandle {
  readonly result = new Emitter<MatchResultMessage>();
  readonly rematchState = new Emitter<RematchStateMessage>();
  readonly opponentLeft = new Emitter<OpponentLeftMessage>();
  private startWaiters: { resolve(t: number): void; reject(e: Error): void }[] = [];
  private startAt: number | null = null;
  private readySent = false;
  private reported = false;
  private resultSeen = false;
  private _closed = false;

  constructor(
    readonly found: MatchFound,
    private readonly conn: MatchConnection,
  ) {}

  get transport(): Promise<Transport> {
    return this.conn.transportReady;
  }

  get transportKind(): TransportKind | null {
    return this.conn.transport.activeKind;
  }

  get closed(): boolean {
    return this._closed;
  }

  /** match_start arrived for this game. */
  get hasStarted(): boolean {
    return this.startAt !== null;
  }

  /** Its server verdict was delivered. */
  get hasResult(): boolean {
    return this.resultSeen;
  }

  ready(): Promise<number> {
    if (this._closed) return Promise.reject(new Error('match closed'));
    if (this.startAt !== null) return Promise.resolve(this.startAt);
    const p = new Promise<number>((resolve, reject) => this.startWaiters.push({ resolve, reject }));
    if (!this.readySent) {
      this.readySent = true;
      if (!this.conn.client.sendMsg({ type: 'match_ready', matchId: this.found.matchId })) {
        this.readySent = false;
        this.fail(new Error('not connected to the lobby'));
      }
    }
    return p;
  }

  /** match_start arrived (server clock → local monotonic clock). */
  started(localStart: number): void {
    this.startAt = localStart;
    for (const w of this.startWaiters.splice(0)) w.resolve(localStart);
  }

  fail(e: Error): void {
    for (const w of this.startWaiters.splice(0)) w.reject(e);
  }

  reportResult(r: MatchResult): void {
    if (this.reported || this._closed) return;
    this.reported = true;
    this.conn.client.sendMsg({
      type: 'result_report',
      matchId: this.found.matchId,
      winner: r.winner,
      score: [Math.max(0, Math.round(r.score[0])), Math.max(0, Math.round(r.score[1]))],
      ticks: r.ticks >>> 0,
      finalHash: r.finalHash >>> 0,
    });
  }

  rematch(want: boolean): void {
    if (this._closed) return;
    this.conn.client.sendMsg({ type: 'rematch', matchId: this.found.matchId, want });
  }

  leave(reason: 'quit' | 'desync' | 'error'): void {
    if (this._closed) return;
    this.conn.client.sendMsg({ type: 'match_leave', matchId: this.found.matchId, reason });
    // A started game is settled by the server (forfeit: you lose): keep listening for that verdict.
    this.conn.client.awaitVerdict(this);
    this.conn.client.closeConnection(this.conn);
  }

  onResult(handler: (msg: MatchResultMessage) => void): () => void {
    return this.result.on(handler);
  }

  onRematchState(handler: (msg: RematchStateMessage) => void): () => void {
    return this.rematchState.on(handler);
  }

  onOpponentLeft(handler: (msg: OpponentLeftMessage) => void): () => void {
    return this.opponentLeft.on(handler);
  }

  deliverResult(msg: MatchResultMessage): void {
    if (this.resultSeen) return;
    this.resultSeen = true;
    this.result.emit(msg);
  }

  close(): void {
    if (this._closed) return;
    this._closed = true;
    this.fail(new Error('match closed'));
  }
}

// ── Match connection (one per matchId, survives rematches) ─────────────────────────────────────────────

class MatchConnection {
  readonly transport: HybridTransport;
  readonly transportReady: Promise<Transport>;
  peer: PeerLink | null = null;
  handle: MatchHandle;
  relayRequested = false;
  relayActive = false;
  closed = false;
  private p2pTimer: TimerId | null = null;
  private resolveTransport!: (t: Transport) => void;
  private rejectTransport!: (e: Error) => void;
  readonly relayCloseHandlers = new Set<() => void>();
  private readonly unsubPath: () => void;

  constructor(
    readonly client: OnlineClientImpl,
    public found: MatchFound,
  ) {
    const env = client.env;
    this.transport = new HybridTransport(() => env.now());
    this.transportReady = new Promise<Transport>((resolve, reject) => {
      this.resolveTransport = resolve;
      this.rejectTransport = reject;
    });
    // Avoid unhandled rejections when nobody awaited the transport before the match was torn down.
    this.transportReady.catch(() => {});
    this.handle = new MatchHandle(found, this);
    this.transport.onStateChange((s) => {
      if (s === 'open') this.resolveTransport(this.transport);
    });
    this.unsubPath = this.transport.onPathChange((kind) => {
      client.onPathChange(this, kind);
      if (kind === 'webrtc') {
        this.clearP2pTimer();
        // Keep the relay on standby. A DataChannel can stay 'open' while delivering nothing (Wi-Fi roam, a dead
        // NAT binding, a one-way path: ICE only reports 'failed' after ~30 s), far past the session's 5 s peer
        // timeout. With the relay attached, the hybrid covers any direct silence within P2P_PROVEN_WINDOW_MS;
        // while the DataChannel delivers, nothing is sent on the relay.
        this.requestRelay('standby');
      }
      // The DataChannel died after it had opened and there is no relay yet: fall back now.
      if (kind === null && this.transport.state === 'open') this.requestRelay('p2p lost');
    });
    this.startP2p();
  }

  get matchId(): string {
    return this.found.matchId;
  }

  private startP2p(): void {
    const env = this.client.env;
    const Ctor = env.RTCPeerConnection;
    if (!Ctor) {
      this.requestRelay('webrtc unavailable');
      return;
    }
    try {
      this.peer = new PeerLink(env, Ctor, {
        polite: this.found.polite,
        iceServers: this.found.iceServers,
        signal: (data: SignalData) => this.client.sendMsg({ type: 'signal', matchId: this.matchId, data }),
        onOpen: (dc) => {
          if (this.closed) return;
          this.transport.attachWebRtc(createWebRtcTransport(dc));
        },
        onFailed: (reason) => this.requestRelay(reason),
      });
    } catch (e) {
      env.log.warn('RTCPeerConnection failed', e);
      this.peer = null;
      this.requestRelay('webrtc error');
      return;
    }
    this.p2pTimer = env.setTimeout(() => {
      this.p2pTimer = null;
      if (this.transport.activeKind !== 'webrtc') this.requestRelay('p2p timeout');
    }, P2P_CONNECT_TIMEOUT_MS);
  }

  private clearP2pTimer(): void {
    if (this.p2pTimer !== null) this.client.env.clearTimeout(this.p2pTimer);
    this.p2pTimer = null;
  }

  requestRelay(reason: string): void {
    if (this.relayRequested || this.closed) return;
    this.relayRequested = true;
    this.client.env.log.info(`relay requested (${reason})`, this.matchId);
    if (!this.client.sendMsg({ type: 'relay_request', matchId: this.matchId })) this.relayRequested = false;
  }

  onRelayReady(): void {
    if (this.closed) return;
    this.relayRequested = true;
    if (this.relayActive) return;
    this.relayActive = true;
    this.transport.attachRelay(createRelayTransport(this.client.relayChannel(this.matchId)));
  }

  onSignal(data: SignalData): void {
    void this.peer?.handle(data);
  }

  /** A rematch: same transport, new handle. */
  nextGame(found: MatchFound): MatchHandle {
    this.found = found;
    this.handle.close();
    this.handle = new MatchHandle(found, this);
    return this.handle;
  }

  /** The relay can no longer carry frames (socket dropped / match over). */
  relayLost(): void {
    if (!this.relayActive) return;
    this.relayActive = false;
    this.relayRequested = false;
    for (const h of [...this.relayCloseHandlers]) h();
    this.relayCloseHandlers.clear();
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.clearP2pTimer();
    this.unsubPath();
    this.handle.close();
    this.relayLost();
    this.transport.close();
    this.peer?.close();
    this.peer = null;
    this.rejectTransport(new Error('match closed'));
  }
}

// ── Client ──────────────────────────────────────────────────────────────────────────────────────────────

export class OnlineClientImpl implements OnlineClient {
  readonly socket: LobbySocket;
  private match: MatchConnection | null = null;
  private readonly matchHandlers = new Emitter<OnlineMatchHandle>();
  private sessionToken: string | null;
  private serverUrl: string | null = null;
  private pendingRating: { resolve(r: RatingInfo): void; reject(e: Error): void }[] = [];
  private pendingBoard: { resolve(r: LeaderboardEntry[]): void; reject(e: Error): void }[] = [];
  private pendingActions: { resolve(r: PlayerActionResult): void; reject(e: Error): void }[] = [];
  /** Closed games still waiting for their `match_result` (by matchId). */
  private readonly verdicts = new Map<string, { handle: MatchHandle; timer: TimerId }>();
  /** In a match, our own server link went quiet: `ui.net` shows 'connecting' (RECONNECTING…) until it answers. */
  private linkDown = false;

  constructor(
    private readonly ui: UiStore,
    readonly env: NetEnv,
    private readonly defaultUrl: () => string,
  ) {
    this.sessionToken = ui.settings.value.sessionToken;
    this.socket = new LobbySocket(env, {
      hello: () => this.hello(),
      onState: (s, m) => this.onSocketState(s, m),
      onLink: (ok) => this.onLink(ok),
    });
    this.wire();
  }

  // ── Public API ────────────────────────────────────────────────────────────────────────────────────

  get connected(): boolean {
    return this.socket.online;
  }

  get clockOffsetMs(): number {
    return this.socket.clock.offsetMs;
  }

  connect(url?: string): Promise<void> {
    const target = url ?? this.serverUrl ?? this.defaultUrl();
    this.serverUrl = target;
    return this.socket.connect(target);
  }

  disconnect(): void {
    this.closeMatch();
    this.clearVerdicts();
    this.socket.close();
    this.patchNet({ status: 'offline', message: undefined, queue: undefined, room: undefined }, true);
  }

  setName(name: string): void {
    const r = PlayerNameSchema.safeParse(name);
    if (!r.success) {
      this.ui.toast('Names: 1–16 letters, numbers, spaces and _ . - ! ?', 'error');
      return;
    }
    this.sendMsg({ type: 'set_name', name: r.data });
  }

  queue(mode: QueueMode, character: CharacterId): void {
    this.sendOrWarn({ type: 'queue_join', mode, character });
  }

  leaveQueue(): void {
    this.sendMsg({ type: 'queue_leave' });
  }

  createRoom(character: CharacterId, settings?: RoomSettings): void {
    this.sendOrWarn({ type: 'room_create', character, ...(settings ? { settings } : {}) });
  }

  joinRoom(code: string, character: CharacterId): void {
    const c = code.trim().toUpperCase();
    if (!/^[A-Z]{4}$/.test(c)) {
      this.ui.toast('Room codes are 4 letters', 'error');
      return;
    }
    this.sendOrWarn({ type: 'room_join', code: c, character });
  }

  setReady(ready: boolean, character: CharacterId): void {
    this.sendMsg({ type: 'room_ready', ready, character });
  }

  leaveRoom(): void {
    this.sendMsg({ type: 'room_leave' });
    this.patchNet({ status: 'online', room: undefined });
  }

  requestLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
    return this.request(this.pendingBoard, {
      type: 'leaderboard_get',
      limit: Math.max(1, Math.min(100, limit)),
    });
  }

  reportPlayer(playerId: string, reason: PlayerReportReason, matchId?: string): Promise<PlayerActionResult> {
    return this.request(this.pendingActions, {
      type: 'player_report',
      playerId,
      reason,
      ...(matchId ? { matchId } : {}),
    });
  }

  blockPlayer(playerId: string, unblock = false): Promise<PlayerActionResult> {
    return this.request(this.pendingActions, {
      type: 'player_block',
      playerId,
      ...(unblock ? { unblock: true } : {}),
    });
  }

  requestRating(): Promise<RatingInfo> {
    return this.request(this.pendingRating, { type: 'rating_get' });
  }

  onMatch(handler: (match: OnlineMatchHandle) => void): () => void {
    return this.matchHandlers.on(handler);
  }

  on<T extends ServerMessageType>(type: T, handler: (msg: MessageOf<ServerMessage, T>) => void): () => void {
    return this.socket.on(type, handler);
  }

  relayChannel(matchId: string): BinaryChannel {
    const socket = this.socket;
    const active = (): MatchConnection | null => {
      const m = this.match;
      return m && !m.closed && m.matchId === matchId && m.relayActive ? m : null;
    };
    return {
      get open() {
        return active() !== null && socket.open;
      },
      send: (data) => {
        if (active()) this.socket.sendBinary(data);
      },
      onBinary: (h) =>
        this.socket.onBinary((data) => {
          if (active()) h(data);
        }),
      onClose: (h) => {
        const m = this.match;
        if (!m || m.matchId !== matchId || m.closed) {
          h();
          return () => {};
        }
        m.relayCloseHandlers.add(h);
        return () => void m.relayCloseHandlers.delete(h);
      },
    };
  }

  // ── Internals used by connections/handles ─────────────────────────────────────────────────────────

  sendMsg(msg: Parameters<LobbySocket['send']>[0]): boolean {
    return this.socket.send(msg);
  }

  closeConnection(conn: MatchConnection): void {
    conn.close();
    if (this.match === conn) {
      this.match = null;
      this.socket.setMatchCadence(false);
      const st = this.ui.net.value.status;
      if (st === 'matched' || st === 'in_match' || (this.linkDown && st === 'connecting'))
        this.patchNet({
          status: this.socket.online ? 'online' : 'offline',
          transport: undefined,
          ...(this.linkDown ? { message: undefined } : {}),
        });
      this.linkDown = false;
    }
  }

  /** Keep a started, unsettled game's handle reachable for its verdict after its connection closes. */
  awaitVerdict(handle: MatchHandle): void {
    if (!handle.hasStarted || handle.hasResult) return;
    const id = handle.found.matchId;
    const prev = this.verdicts.get(id);
    if (prev) this.env.clearTimeout(prev.timer);
    const timer = this.env.setTimeout(() => {
      if (this.verdicts.get(id)?.handle === handle) this.verdicts.delete(id);
    }, VERDICT_WAIT_MS);
    this.verdicts.set(id, { handle, timer });
  }

  private clearVerdicts(): void {
    for (const v of this.verdicts.values()) this.env.clearTimeout(v.timer);
    this.verdicts.clear();
  }

  onPathChange(conn: MatchConnection, kind: TransportKind | null): void {
    if (this.match !== conn) return;
    this.patchNet({ transport: kind === 'webrtc' || kind === 'relay' ? kind : undefined });
  }

  // ── Wiring ────────────────────────────────────────────────────────────────────────────────────────

  private hello(): HelloMessage {
    const name = PlayerNameSchema.safeParse(this.ui.settings.value.playerName);
    return {
      type: 'hello',
      protocol: PROTOCOL_VERSION,
      clientVersion: this.env.clientVersion ?? clientVersionFor(getPlatform().config),
      contentHash: SIM_CONTENT_HASH,
      platform: this.env.platform ?? 'web',
      ...(this.env.appVersion ? { appVersion: this.env.appVersion } : {}),
      ...(name.success ? { name: name.data } : {}),
      ...(this.sessionToken ? { token: this.sessionToken } : {}),
    };
  }

  private sendOrWarn(msg: Parameters<LobbySocket['send']>[0]): void {
    if (!this.sendMsg(msg)) this.ui.toast('Not connected to the game server', 'error');
  }

  private request<T>(
    queue: { resolve(r: T): void; reject(e: Error): void }[],
    msg: Parameters<LobbySocket['send']>[0],
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!this.sendMsg(msg)) {
        reject(new Error('not connected'));
        return;
      }
      const entry = {
        resolve: (r: T) => {
          this.env.clearTimeout(timer);
          resolve(r);
        },
        reject: (e: Error) => {
          this.env.clearTimeout(timer);
          reject(e);
        },
      };
      const timer = this.env.setTimeout(() => {
        const i = queue.indexOf(entry);
        if (i >= 0) queue.splice(i, 1);
        reject(new Error('request timed out'));
      }, REQUEST_TIMEOUT_MS);
      queue.push(entry);
    });
  }

  private patchNet(patch: Partial<NetUiState>, replace = false): void {
    const base = replace ? { status: this.ui.net.value.status } : this.ui.net.value;
    const next: NetUiState = { ...base, ...patch } as NetUiState;
    for (const k of Object.keys(next) as (keyof NetUiState)[]) if (next[k] === undefined) delete next[k];
    this.ui.net.value = next;
  }

  /**
   * Our own link to the server (pings unanswered) during a match: a stalled match is OUR connection's fault, not the
   * opponent's. 'connecting' makes the match strip read RECONNECTING… instead of WAITING FOR OPPONENT… (QA r4); the
   * first message back restores 'in_match'. Peer silence alone (our pongs still arrive) keeps 'in_match'.
   */
  private onLink(ok: boolean): void {
    if (!ok) {
      if (this.ui.net.value.status !== 'in_match' || !this.match || this.match.closed) return;
      this.linkDown = true;
      this.env.log.warn('server link silent during the match', this.match.matchId);
      this.patchNet({ status: 'connecting', message: 'Your connection dropped. Reconnecting…' });
      return;
    }
    if (!this.linkDown) return;
    this.linkDown = false;
    if (this.ui.net.value.status === 'connecting' && this.match && !this.match.closed)
      this.patchNet({ status: 'in_match', message: undefined });
  }

  private onSocketState(s: SocketState, message?: string): void {
    if (s !== 'online') this.linkDown = false;
    switch (s) {
      case 'connecting':
        this.patchNet({ status: 'connecting', message: message ?? 'Connecting…' });
        break;
      case 'online':
        this.patchNet({ status: this.match ? this.ui.net.value.status : 'online', message: undefined });
        break;
      case 'reconnecting':
        // The relay path dies with the socket; a P2P match keeps running on its own.
        this.match?.relayLost();
        this.patchNet({
          status: 'connecting',
          message: message ?? 'Reconnecting…',
          queue: undefined,
          room: undefined,
        });
        break;
      case 'idle':
        this.closeMatch();
        this.clearVerdicts();
        this.patchNet({ status: message ? 'error' : 'offline', message, queue: undefined, room: undefined });
        this.rejectPending(new Error(message ?? 'disconnected'));
        break;
      case 'fatal':
        this.closeMatch();
        this.clearVerdicts();
        this.patchNet({
          status: 'error',
          message: message ?? 'Disconnected',
          queue: undefined,
          room: undefined,
        });
        this.rejectPending(new Error(message ?? 'fatal'));
        break;
    }
  }

  private rejectPending(e: Error): void {
    for (const p of this.pendingRating.splice(0)) p.reject(e);
    for (const p of this.pendingBoard.splice(0)) p.reject(e);
    for (const p of this.pendingActions.splice(0)) p.reject(e);
  }

  private closeMatch(): void {
    if (this.match) this.closeConnection(this.match);
  }

  private wire(): void {
    const s = this.socket;
    s.on('welcome', (m) => {
      const reconnected = this.match !== null;
      // A different token while we sent ours = our session is live in another tab: keep the stored identity for
      // that tab and use the new one only in memory.
      const sentToken = this.sessionToken;
      this.sessionToken = m.token;
      if (!sentToken || m.token === sentToken) {
        if (this.ui.settings.value.sessionToken !== m.token)
          this.ui.updateSettings({ sessionToken: m.token });
      }
      // The server dropped any match we were in when the old socket died.
      if (reconnected) this.closeMatch();
      this.patchNet({
        status: 'online',
        message: undefined,
        playerId: m.playerId,
        name: m.name,
        rating: m.rating,
        queue: undefined,
        room: undefined,
      });
    });
    s.on('pong', () => {
      const rtt = Math.round(s.clock.rttMs);
      if (this.ui.net.value.rttMs !== rtt) this.patchNet({ rttMs: rtt });
    });
    s.on('name_set', (m) => this.patchNet({ name: m.name }));
    s.on('error', (m) => {
      // The socket handles these (fatal: reload / update).
      if (m.code === 'version_mismatch' || m.code === 'protocol_mismatch' || m.code === 'update_required')
        return;
      const text = ERROR_TEXT[m.code] ?? m.message;
      if (m.code === 'rate_limited') this.env.log.warn('rate limited by the server');
      else this.ui.toast(text, 'error');
      if (m.ref === 'room_join' || m.ref === 'room_create')
        this.patchNet({ status: 'online', room: undefined });
      if (m.ref === 'queue_join' && m.code !== 'already_queued')
        this.patchNet({ status: 'online', queue: undefined });
    });
    s.on('queue_status', (m) => {
      if (m.state === 'searching')
        this.patchNet({
          status: 'queueing',
          queue: { mode: m.mode, waitingMs: m.waitingMs, playersInQueue: m.playersInQueue },
        });
      else if (this.ui.net.value.status === 'queueing') this.patchNet({ status: 'online', queue: undefined });
    });
    s.on('room_state', (m) => {
      this.patchNet({
        status: this.match && !this.match.closed ? this.ui.net.value.status : 'in_room',
        room: { code: m.code, hostId: m.hostId, players: m.players },
      });
    });
    s.on('room_closed', (m) => {
      this.patchNet({ status: this.match ? this.ui.net.value.status : 'online', room: undefined });
      if (m.reason !== 'left') this.ui.toast(`Room ${m.code} closed (${m.reason})`, 'info');
    });
    s.on('rating', (m) => {
      this.patchNet({ rating: m.rating });
      for (const p of this.pendingRating.splice(0)) p.resolve(m.rating);
    });
    s.on('leaderboard', (m) => {
      for (const p of this.pendingBoard.splice(0)) p.resolve(m.entries);
    });
    // Replies come back in order (one socket, handled sequentially per action on the server).
    s.on('player_action', (m) => this.pendingActions.shift()?.resolve(m));
    s.on('match_found', (m) => this.onMatchFound(m));
    s.on('signal', (m) => {
      if (this.match?.matchId === m.matchId) this.match.onSignal(m.data);
    });
    s.on('relay_ready', (m) => {
      if (this.match?.matchId === m.matchId) this.match.onRelayReady();
    });
    s.on('match_start', (m) => {
      const conn = this.match;
      if (!conn || conn.matchId !== m.matchId) return;
      this.patchNet({ status: 'in_match', queue: undefined });
      s.setMatchCadence(true);
      conn.handle.started(s.clock.toLocal(m.startAt));
    });
    s.on('match_result', (m) => {
      const conn = this.match;
      const live = conn && !conn.closed && conn.matchId === m.matchId ? conn : null;
      const waiting = this.verdicts.get(m.matchId);
      if (!live && !waiting) return;
      if (waiting) {
        this.env.clearTimeout(waiting.timer);
        this.verdicts.delete(m.matchId);
      }
      // A closed game (opponent left / we quit) still gets its verdict: forfeit toasts, Results status, rating.
      (live ? live.handle : waiting!.handle).deliverResult(m);
      if (m.rating) {
        const r = this.ui.net.value.rating;
        if (r) this.patchNet({ rating: { ...r, rating: m.rating.after } });
      }
      if (live) {
        s.setMatchCadence(false);
        this.linkDown = false;
        this.patchNet({ status: 'online', message: undefined });
      }
    });
    s.on('rematch_state', (m) => {
      const conn = this.match;
      if (!conn || conn.matchId !== m.matchId) return;
      conn.handle.rematchState.emit(m);
      if (m.declined) this.closeConnection(conn);
    });
    s.on('opponent_left', (m) => {
      const conn = this.match;
      if (!conn || conn.matchId !== m.matchId) return;
      conn.handle.opponentLeft.emit(m);
      // The server sends opponent_left before the forfeit verdict: keep listening for it.
      this.awaitVerdict(conn.handle);
      this.closeConnection(conn);
    });
  }

  private onMatchFound(m: MatchFound): void {
    const conn = this.match;
    if (m.rematch && conn && !conn.closed && conn.matchId === m.matchId) {
      const handle = conn.nextGame(m);
      this.patchNet({ status: 'matched', queue: undefined });
      this.matchHandlers.emit(handle);
      return;
    }
    if (conn) this.closeConnection(conn);
    const next = new MatchConnection(this, m);
    this.match = next;
    this.patchNet({ status: 'matched', queue: undefined, transport: undefined });
    this.matchHandlers.emit(next.handle);
  }
}

const ERROR_TEXT: Partial<Record<string, string>> = {
  room_not_found: 'No room with that code',
  room_full: 'That room is full',
  build_mismatch: 'That room is on a different game version. Both players need the latest version.',
  already_queued: 'Already searching for a match',
  already_in_match: 'You are already in a match',
  not_in_room: 'You are not in a room',
  not_in_match: 'That match is over',
  internal: 'Server error — try again',
  not_welcomed: 'Still connecting…',
  bad_message: 'The server rejected a message',
};
